import { AttachmentsSelector } from "PFApp/components/activity_form/form_selectors/attachments_selector";
import { AddAttachmentButton } from "PFApp/components/activity_form/form_selectors/attachments_selector/add_attachment_button";
import { FIELD_ATTACHMENT_IDS_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { Inline } from "PFComponents/containers";
import { CountTag } from "PFComponents/count_tag";
import { Attachment } from "PFCore/types";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useActivityFormStore } from "../../../activity_form_store";
import { FormSection, SectionProps } from "../../form_section";

export const AttachmentsFormSection = ({ id, title }: SectionProps) => {
  const attachments = useActivityFormStore((state) => state.attachments);
  const qaIdPrefix = useActivityFormStore((state) => state.qaIdPrefix);

  const [localAttachments, setLocalAttachments] = useState<Attachment[]>(attachments);

  const { setValue, setError } = useFormContext();

  const attachmentKeys = useWatch({ name: FIELD_ATTACHMENT_IDS_KEY });
  const attachmentsCount = attachmentKeys.length;

  const handleAttachmentsChange = (updatedAttachments: Attachment[]) => {
    setValue(
      FIELD_ATTACHMENT_IDS_KEY,
      updatedAttachments.map(({ id }) => id),
      { shouldDirty: true }
    );
    setLocalAttachments(updatedAttachments);
  };

  const handleAttachementRemove = (attachment: Attachment) => {
    const updatedAttachments = localAttachments.filter(({ id }) => id !== attachment.id);
    handleAttachmentsChange(updatedAttachments);
  };

  const handleUploadStart = (pendingAttachment: Attachment) => {
    setLocalAttachments((prev) => [...prev, pendingAttachment]);
  };

  const handleUploadSuccess = (attachment: Attachment, pendingAttachment: Attachment) => {
    const newAttachments = localAttachments.filter((att) => att.id !== pendingAttachment.id);
    const updatedAttachments = [...newAttachments, attachment];
    setValue(
      FIELD_ATTACHMENT_IDS_KEY,
      updatedAttachments.map(({ id }) => id),
      { shouldDirty: true }
    );
    setLocalAttachments(updatedAttachments);
  };

  const handleUploadError = (pendingAttachment: Attachment, errorMessage: string) => {
    setError(FIELD_ATTACHMENT_IDS_KEY, { message: errorMessage });
    handleAttachementRemove(pendingAttachment);
  };

  const addAttachmentAction = () => (
    <AddAttachmentButton
      onUploadStart={handleUploadStart}
      onUploadSuccess={handleUploadSuccess}
      onUploadError={handleUploadError}
    />
  );

  const renderHeader = () => (
    <Inline alignItems="center">
      {title}
      <CountTag count={attachmentsCount} />
    </Inline>
  );

  return (
    <FormSection
      id={id}
      title={renderHeader()}
      actions={addAttachmentAction()}
      qaId={`${qaIdPrefix}-attachments`}
    >
      <AttachmentsSelector attachments={localAttachments} onChange={handleAttachmentsChange} />
    </FormSection>
  );
};
